import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { FiShoppingBag, FiMenu, FiX, FiSearch, FiUser } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setIsCartOpen, cartCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop All', path: '/shop' },
    { name: 'Recipe Kits', path: '/shop?cat=recipe-kit' },
    { name: 'Support', path: '/support' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white/80 backdrop-blur-xl border-b border-gray-100 py-3' : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        
        <Link to="/" className="flex items-center gap-2 group relative z-10 focus:ring-2 focus:ring-bloom-coral rounded-xl p-1 outline-none">
          <div className="w-10 h-10 bg-bloom-coral rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform shadow-lg shadow-bloom-coral/20">
            <span className="text-white font-bold text-xl">B</span>
          </div>
          <span className="font-heading font-bold text-2xl text-bloom-charcoal tracking-tight hidden sm:block">
            Bento Bloom
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map(link => (
            <Link 
              key={link.path} 
              to={link.path}
              className={`font-semibold text-sm uppercase tracking-widest transition-all hover:text-bloom-coral focus:text-bloom-coral outline-none ${
                location.pathname === link.path ? 'text-bloom-coral' : 'text-gray-500'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-6 relative z-10">
          <button className="p-2 text-gray-500 hover:text-bloom-charcoal focus:text-bloom-coral outline-none hidden sm:block" aria-label="Search">
            <SafeIcon icon={FiSearch} className="text-xl" />
          </button>
          <button className="p-2 text-gray-500 hover:text-bloom-charcoal focus:text-bloom-coral outline-none hidden sm:block" aria-label="Account">
            <SafeIcon icon={FiUser} className="text-xl" />
          </button>
          <button 
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-bloom-charcoal hover:scale-110 focus:scale-110 transition-transform outline-none" 
            aria-label={`Open cart, ${cartCount} items`}
          >
            <SafeIcon icon={FiShoppingBag} className="text-2xl" />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-bloom-yellow text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button 
            className="lg:hidden p-2 text-gray-500"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 p-6 flex flex-col gap-6"
          >
            {navLinks.map(link => (
              <Link 
                key={link.path} 
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="font-heading font-bold text-2xl text-gray-800 hover:text-bloom-coral"
              >
                {link.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}